import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { CheckInStatus, Event } from '../types/event.types';
import { FamilyMember } from '../types/family.types';
import { validateEventAttendees } from '../utils/eventValidation';

interface UseEventFlowReturn {
  selectedMembers: string[];
  memberDetails: FamilyMember[];
  isSelectingForFamily: boolean;
  checkInStatus: CheckInStatus;
  handleEventSelect: (eventId: string, events: Event[]) => void;
  handleCheckIn: (eventId: string, events: Event[]) => void;
  isEventCheckedIn: (eventId: string) => boolean;
}

export const useEventFlow = (): UseEventFlowReturn => {
  const location = useLocation();
  const navigate = useNavigate();

  const selectedMembers: string[] = location.state?.selectedMembers || [];
  const memberDetails: FamilyMember[] = (location.state?.memberDetails || []) as FamilyMember[];
  const isSelectingForFamily = selectedMembers.length > 0;

  const [checkInStatus] = useState<CheckInStatus>({});

  const handleEventSelect = (eventId: string, events: Event[]) => {
    const selectedEvent = events.find((e) => e.id === eventId);
    if (!selectedEvent) {
      return;
    }

    navigate('/my-families', {
      state: {
        selectedEvent: selectedEvent,
        returnTo: '/events',
      },
    });
  };

  const handleCheckIn = (eventId: string, events: Event[]) => {
    if (selectedMembers.length === 0) {
      return;
    }

    const selectedEvent = events.find((e) => e.id === eventId);

    if (!selectedEvent) {
      return;
    }

    const validation = validateEventAttendees(selectedEvent, memberDetails);

    if (validation.compatibleMembers.length === 0) {
      return;
    }

    navigate('/check-in-confirmation', {
      state: {
        event: selectedEvent,
        selectedMembers: validation.compatibleMembers.map((m) => m.id),
        memberDetails: validation.compatibleMembers,
      },
    });
  };

  const isEventCheckedIn = (eventId: string): boolean => {
    return checkInStatus[eventId] && selectedMembers.every((memberId: string) => checkInStatus[eventId][memberId]);
  };

  return {
    selectedMembers,
    memberDetails,
    isSelectingForFamily,
    checkInStatus,
    handleEventSelect,
    handleCheckIn,
    isEventCheckedIn,
  };
};
